<template>
  <div class="tw-space-y-8">
    <!-- Chart Display -->
    <div class="tw-mt-6">
      <!-- Chart Controls -->
      <div v-if="chartView === 'detail'" class="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <Button
          @click="chartView = 'summary'"
          icon-before="fas fa-arrow-left"
        >
          View All Scenarios
        </Button>
      </div>

      <!-- Summary Bar Chart -->
      <div v-if="chartView === 'summary'">
        <HighChartsWrapper
          name="Stress Test — Total Returns"
          :chart-type="'chart'"
          :chart-options="multiScenarioBarChartOptions"
          export-image
          @image-changed="(data) => checkupStore.updateSlideImage(data, 'Stress Test')"
          @image-removed="(data) => checkupStore.removeSlideImage(data)"
        />
      </div>

      <!-- Detail Line Chart -->
      <div v-if="chartView === 'detail' && detailScenario">
        <HighChartsWrapper
          ref="detailChart"
          :key="detailChartKey"
          name="Stress Test — Portfolio Performance Over Time"
          :chart-type="'line'"
          :chart-options="detailLineChartOptions"
        />
        <div class="tw-text-xs tw-text-gray-500 tw-mt-2">
          Showing portfolio performance evolution over time for {{ detailScenario.name }}. 
          Values show percentage return from the starting point.
        </div>
      </div>
    </div>
    
    <!-- Controls -->
    <div class="tw-space-y-4">
      <!-- Add Custom Scenario -->
      <div>
        <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-3">Add Custom Scenario</h3>
        <div class="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
          <Field>
            <Label>Scenario name</Label>
            <TextInput v-model="custom.name" placeholder="My scenario"/>
          </Field>
          <Field>
            <Label>Start date</Label>
            <TextInput v-model="custom.start" type="date" />
          </Field>
          <Field>
            <Label>End date</Label>
            <TextInput v-model="custom.end" type="date" />
          </Field>
          <Button @click="addCustomScenario" variant="primary">
            Add Scenario
          </Button>
        </div>
      </div>
    </div>

    <!-- Scenarios Table -->
    <div class="tw-overflow-x-auto">
      <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead class="tw-bg-gray-50">
          <tr>
            <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600">Scenario</th>
            <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600">Start</th>
            <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-600">End</th>
            <th v-if="portfolioEntity" class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600" :title="portfolioEntity.name">
              {{ truncateName(portfolioEntity.name) }}
            </th>
            <th v-if="benchmarkEntity" class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600" :title="benchmarkEntity.name">
              {{ truncateName(benchmarkEntity.name) }}
            </th>
            <th v-if="optimizedEntity" class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600" :title="optimizedEntity.name">
              {{ truncateName(optimizedEntity.name) }}
            </th>
            <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody class="tw-divide-y tw-divide-gray-200">
          <tr v-for="sc in scenarios" :key="sc.id" class="hover:tw-bg-gray-50 tw-transition">
            <td class="tw-px-4 tw-py-3 tw-font-medium">{{ sc.name }}</td>
            <td class="tw-px-4 tw-py-3">{{ formatTableDate(sc.start) }}</td>
            <td class="tw-px-4 tw-py-3">{{ formatTableDate(sc.end) }}</td>
            <td v-if="portfolioEntity" class="tw-px-4 tw-py-3">
              <span v-if="loadingCell(sc.id, 'portfolio')" class="tw-text-gray-400">…</span>
              <span v-else :class="retClass(result(sc.id, 'portfolio'))">{{ formatPct(result(sc.id, 'portfolio')) }}</span>
            </td>
            <td v-if="benchmarkEntity" class="tw-px-4 tw-py-3">
              <span v-if="loadingCell(sc.id, 'benchmark')" class="tw-text-gray-400">…</span>
              <span v-else :class="retClass(result(sc.id, 'benchmark'))">{{ formatPct(result(sc.id, 'benchmark')) }}</span>
            </td>
            <td v-if="optimizedEntity" class="tw-px-4 tw-py-3">
              <span v-if="loadingCell(sc.id, 'optimized')" class="tw-text-gray-400">…</span>
              <span v-else :class="retClass(result(sc.id, 'optimized'))">{{ formatPct(result(sc.id, 'optimized')) }}</span>
            </td>
            <td class="tw-px-4 tw-py-3 tw-text-right tw-space-x-2">
              <Buttons size="3">
                <Button
                  @click="viewScenarioDetail(sc.id)"
                  :disabled="!hasScenarioData(sc.id)"
                >
                  View detail
                </Button>

                <Button
                  @click="removeScenario(sc.id)"
                  aria-label="Remove scenario"
                  variant="danger"
                  outlined
                >
                  Remove
                </Button>
              </Buttons>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Analysis Settings -->
    <div class="tw-flex tw-items-end tw-gap-4 tw-pt-4 tw-border-t">
      <Field>
        <Label>Rebalance Frequency</Label>
        <Select
          v-model="rebalance"
          @change="onRebalanceChange"
          :options="[
            { value: 'none', label: 'None' },
            { value: 'annual', label: 'Annual' },
            { value: 'monthly', label: 'Monthly' },
          ]"
        />
      </Field>
    </div>
  </div>
</template>

<script>
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { Button, Buttons, Field, Label, Select, TextInput } from '@stakeui';

export default {
  name: 'StressTestFeatureComponent',
  components: {
    Button,
    Buttons,
    Field,
    Label,
    Select,
    TextInput,
    HighChartsWrapper,
  },
  data() {
    return {
      rebalance: 'annual',
      chartView: 'summary', // 'summary' or 'detail'
      detailScenarioId: null,
      custom: {
        name: '',
        start: '',
        end: '',
      },
      // Pre-populated scenarios (historical)
      scenarios: [
        // Dates are inclusive ISO; adjust if your backend expects otherwise.
        { id: 'inflation-2022', name: '2022 Inflation Crash', start: '2022-01-01', end: '2022-12-31' },
        { id: 'pandemic-2020', name: '2020 Pandemic Crash', start: '2020-02-20', end: '2020-03-23' },
        { id: 'bull-2013', name: '2013 Bull Market', start: '2013-01-01', end: '2013-12-31' },
        { id: 'taper-2013', name: '2013 Interest Rate Hike', start: '2013-05-01', end: '2013-09-05' },
        { id: 'bear-2008', name: '2008 Bear Market', start: '2008-01-01', end: '2008-12-31' },
        { id: 'gfc-2007-2009', name: '2007-2009 Great Financial Crisis', start: '2007-10-15', end: '2009-03-02' },
      ],
      results: {},
      detailedResults: {},
      loading: {},
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore),

    portfolioEntity() {
      return this.checkupStore?.portfolio || null;
    },
    benchmarkEntity() {
      return this.checkupStore?.benchmark || null;
    },
    optimizedEntity() {
      return this.checkupStore?.optimizedPortfolio || null;
    },

    detailScenario() {
      return this.scenarios.find(s => s.id === this.detailScenarioId) || null;
    },
    multiScenarioBarChartOptions() {
      // Create categories from scenario names
      const categories = this.scenarios.map(sc => sc.name);
      
      // Build series data for Portfolio, Benchmark, and Optimized
      const series = [];

      if (this.portfolioEntity) {
        const portfolioData = this.scenarios.map(sc => {
          const val = this.toPctNum(this.result(sc.id, 'portfolio'));
          return val !== null ? val : null;
        });
        
        series.push({
          name: this.portfolioEntity.name || 'Portfolio',
          data: portfolioData,
          color: this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#111827'
        });
      }

      if (this.benchmarkEntity) {
        const benchmarkData = this.scenarios.map(sc => {
          const val = this.toPctNum(this.result(sc.id, 'benchmark'));
          return val !== null ? val : null;
        });
        
        series.push({
          name: this.benchmarkEntity.name || 'Benchmark',
          data: benchmarkData,
          color: this.advisorStore.firmAdvisorCoreSettings?.benchmark_color || '#2563eb'
        });
      }

      if (this.optimizedEntity) {
        const optimizedData = this.scenarios.map(sc => {
          const val = this.toPctNum(this.result(sc.id, 'optimized'));
          return val !== null ? val : null;
        });
        
        series.push({
          name: this.optimizedEntity.name || 'Proposed',
          data: optimizedData,
          color: this.advisorStore.firmAdvisorCoreSettings?.proposed_color || '#16a34a'
        });
      }

      return {
        chart: { 
          type: 'column', 
          height: 500,
          marginBottom: 120
        },
        title: { text: 'Portfolio performance under various market scenarios' },
        xAxis: {
          categories: categories,
          title: { text: null },
          labels: {
            rotation: -25,
            style: {
              fontSize: '16px'
            },
            y: 15
          }
        },
        yAxis: {
          title: { text: 'Total Return (%)' },
          labels: { 
            formatter() { 
              return `${this.value}%`; 
            } 
          },
          plotLines: [{
            value: 0,
            color: '#666',
            dashStyle: 'dash',
            width: 1
          }]
        },
        series,
        tooltip: {
          shared: true,
          formatter() {
            let tooltip = `<b>${this.x}</b><br/>`;
            this.points.forEach(point => {
              if (point.y !== null) {
                tooltip += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y.toFixed(2)}%</b><br/>`;
              }
            });
            return tooltip;
          }
        },
        legend: { 
          enabled: true,
          align: 'center',
          verticalAlign: 'top',
          y: 20
        },
        credits: { enabled: false },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              formatter() { 
                return this.y !== null ? `${this.y.toFixed(1)}%` : ''; 
              },
              style: {
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#333333'
              }
            },
            groupPadding: 0.1,
            pointPadding: 0.05
          }
        }
      };
    },

    detailLineChartOptions() {
      if (!this.detailScenario) {
        return {
          chart: { type: 'line', height: 420 },
          title: { text: 'Select a scenario to view the cumulative path' },
          series: [],
          credits: { enabled: false },
        };
      }

      const sid = this.detailScenario.id;
      const d = this.detailedResults[sid] || {};
      const startTs = new Date(this.detailScenario.start).getTime();
      const endTs   = new Date(this.detailScenario.end).getTime();

      // Convert [{x, y}=pv] -> [[ts, pct]], pct = (pv - 1)*100.
      // Anchor 0% at start date and relabel the final monthly point to endTs.
      const toCumPctFromPV = (pvArr) => {
        const map = new Map();
        map.set(startTs, 0); // 0% at exact scenario start

        let lastTs = null;
        let lastVal = null;
        for (const p of (pvArr || [])) {
          if (!p || p.y == null || isNaN(p.y)) continue;
          const ts = new Date(p.x).getTime();       // month-end stamp (e.g., 2020-03-31)
          const pct = (p.y - 1) * 100;              // growth from window start
          map.set(ts, pct);
          lastTs = ts;
          lastVal = pct;
        }

        let arr = Array.from(map.entries()).sort((a, b) => a[0] - b[0]);

        // If the final point is labeled after the true end date (month-end),
        // move that timestamp to the actual end date but keep the same value.
        if (lastTs != null && endTs < lastTs) {
          arr[arr.length - 1] = [endTs, lastVal];
        }

        return arr;
      };

      const series = [];
      if (d.portfolio?.portfolio_values?.length) {
        series.push({
          type: 'line',
          name: this.portfolioEntity?.name || 'Portfolio',
          data: toCumPctFromPV(d.portfolio.portfolio_values),
          color: this.advisorStore?.firmAdvisorCoreSettings?.portfolio_color || '#111827',
        });
      }
      if (d.benchmark?.portfolio_values?.length) {
        series.push({
          type: 'line',
          name: this.benchmarkEntity?.name || 'Benchmark',
          data: toCumPctFromPV(d.benchmark.portfolio_values),
          color: this.advisorStore?.firmAdvisorCoreSettings?.benchmark_color || '#2563eb',
        });
      }
      if (d.optimized?.portfolio_values?.length) {
        series.push({
          type: 'line',
          name: this.optimizedEntity?.name || 'Optimized',
          data: toCumPctFromPV(d.optimized.portfolio_values),
          color: this.advisorStore?.firmAdvisorCoreSettings?.proposed_color || '#16a34a',
        });
      }

      return {
        chart: { type: 'line', height: 420 },
        title: { text: `${this.detailScenario.name} - Portfolio Performance Over Time` },
        xAxis: {
          type: 'datetime',
          title: { text: 'Date' },
          dateTimeLabelFormats: { month: '%b %Y', year: '%Y' },
          min: startTs,
          max: endTs,
        },
        yAxis: {
          title: { text: 'Total Return (%)' },
          labels: { formatter() { return `${this.value}%`; } },
          plotLines: [{
            value: 0, color: '#666', dashStyle: 'dash', width: 1,
            label: { text: 'Starting Value (0%)', align: 'right', style: { color: '#666' } }
          }],
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter() {
            const d = (typeof Highcharts !== 'undefined' && Highcharts.dateFormat)
              ? Highcharts.dateFormat('%Y-%m-%d', this.x)
              : new Date(this.x).toISOString().slice(0, 10);
            let html = `<b>${d}</b><br/>`;
            this.points.forEach(p => {
              if (p.y != null) html += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y.toFixed(2)}%</b><br/>`;
            });
            return html;
          },
        },
        legend: { enabled: true },
        credits: { enabled: false },
        series,
      };
    },
    detailChartKey() {
      const s = this.detailScenario;
      return s ? `${s.id}|${s.start}|${s.end}|${this.rebalance}` : 'no-detail';
    },

  },
  methods: {
    // ========= UX Helpers =========
    onRebalanceChange() {
      this.$emit('rebalance-changed', this.rebalance);
      this.runAll();
    },
    addCustomScenario() {
      const name = (this.custom.name || '').trim();
      const start = (this.custom.start || '').trim();
      const end = (this.custom.end || '').trim();
      if (!name || !start || !end) return;

      const id = `custom-${Date.now()}`;
      this.scenarios.unshift({ id, name, start, end });
      this.custom = { name: '', start: '', end: '' };
      this.runScenario(id);
    },
    removeScenario(id) {
      const idx = this.scenarios.findIndex(s => s.id === id);
      if (idx === -1) return;

      // remove from the list
      this.scenarios.splice(idx, 1);

      // clear reactive caches
      if (this.results[id]) this.$delete(this.results, id);
      if (this.detailedResults[id]) this.$delete(this.detailedResults, id);
      if (this.loading[id]) this.$delete(this.loading, id);

      // if we were viewing this scenario, go back to summary
      if (this.detailScenarioId === id) {
        this.detailScenarioId = null;
        this.chartView = 'summary';
      }
    },
    clearResults() {
      this.results = {};
      this.detailedResults = {};
      this.loading = {};
      this.chartView = 'summary';
      this.detailScenarioId = null;
    },
    viewScenarioDetail(scenarioId) {
      this.detailScenarioId = scenarioId;
      this.chartView = 'detail';
    },
    hasScenarioData(scenarioId) {
      const results = this.results[scenarioId];
      return results && (results.portfolio !== null || results.benchmark !== null || results.optimized !== null);
    },
    formatPct(val) {
      if (val === null || typeof val !== 'number' || isNaN(val)) return '—';
      return (val).toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 2 });
    },
    retClass(val) {
      if (val === null || typeof val !== 'number' || isNaN(val)) return 'tw-text-gray-400';
      return val >= 0 ? 'tw-text-emerald-700 tw-font-medium' : 'tw-text-rose-700 tw-font-medium';
    },
    toPctNum(totalReturn) {
      if (totalReturn === null || typeof totalReturn !== 'number' || isNaN(totalReturn)) return null;
      return totalReturn * 100.0;
    },

    result(sid, key) {
      return this.results?.[sid]?.[key] ?? null;
    },
    setResult(sid, key, value) {
      if (!this.results[sid]) this.$set(this.results, sid, {});
      this.$set(this.results[sid], key, value);
    },
    setDetailedResult(sid, key, value) {
      if (!this.detailedResults[sid]) this.$set(this.detailedResults, sid, {});
      this.$set(this.detailedResults[sid], key, value);
    },
    setLoading(sid, key, value) {
      if (!this.loading[sid]) this.$set(this.loading, sid, {});
      this.$set(this.loading[sid], key, !!value);
    },
    loadingCell(sid, key) {
      return !!(this.loading?.[sid]?.[key]);
    },
    truncateName(name) {
      if (!name) return '';
      return name.length > 50 ? `${name.substring(0, 47)}...` : name;
    },
    
    formatTableDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // fallback to original string if invalid
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}/${day}/${year}`;
    },

    // ========= Data Fetching =========
    async runAll() {
      const ids = this.scenarios.map(s => s.id);
      const promises = [];
      for (const id of ids) {
        promises.push(this.runScenario(id));
      }
      
      // Wait for all scenarios to complete before updating
      await Promise.all(promises);
      
      // Update checkupStore with formatted results for export
      this.updateStressTestResults();
    },

    updateStressTestResults() {
      const formattedResults = this.scenarios.map(scenario => {
        const portfolioResult = this.result(scenario.id, 'portfolio');
        const benchmarkResult = this.result(scenario.id, 'benchmark');
        const optimizedResult = this.result(scenario.id, 'optimized');

        return {
          scenario: scenario.name,
          portfolio_impact: portfolioResult !== null ? this.formatPct(portfolioResult) : '—',
          benchmark_impact: benchmarkResult !== null ? this.formatPct(benchmarkResult) : '—',
          optimized_impact: optimizedResult !== null ? this.formatPct(optimizedResult) : '—',
        };
      });

      // Also store the chart data and rebalance frequency for export
      this.checkupStore.setStressTestResults(formattedResults);
      this.checkupStore.setStressTestChartData({
        chartOptions: this.multiScenarioBarChartOptions,
        rebalanceFrequency: this.rebalance
      });
    },

    async runScenario(scenarioId) {
      const sc = this.scenarios.find(s => s.id === scenarioId);
      if (!sc) return;

      const tasks = [];
      if (this.portfolioEntity) {
        tasks.push(this.runForEntity(scenarioId, 'portfolio', this.portfolioEntity, sc.start, sc.end));
      }
      if (this.benchmarkEntity) {
        tasks.push(this.runForEntity(scenarioId, 'benchmark', this.benchmarkEntity, sc.start, sc.end));
      }
      if (this.optimizedEntity) {
        tasks.push(this.runForEntity(scenarioId, 'optimized', this.optimizedEntity, sc.start, sc.end));
      }

      await Promise.all(tasks);
      
      // Update export data after each scenario completes
      this.updateStressTestResults();
    },

    async runForEntity(scenarioId, key, entity, start, end) {
      const entityExternalId = entity?.external_id ?? entity?.id;
      if (!entityExternalId) {
        this.setResult(scenarioId, key, null);
        return;
      }

      const stillExists = () => this.scenarios.some(s => s.id === scenarioId);

      this.setLoading(scenarioId, key, true);
      console.error('THIS REBALANCE FREQ IS BEING SENT', this.rebalance);
      try {
        const res = await fetch('/api/v1/stress_test/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            entity_external_id: entityExternalId,
            start_date: start,
            end_date: end,
            scenario_type: 'historical',
            return_basis: 'historical',
            rebalance_freq: this.rebalance,
            starting_weights: 'current',
          }),
        });
        const payload = await res.json();

        // bail if the scenario was removed while we were fetching
        if (!stillExists()) return;

        const totalReturn = (typeof payload.total_return === 'number') ? payload.total_return : null;
        this.setResult(scenarioId, key, totalReturn);

        this.setDetailedResult(scenarioId, key, {
          portfolio_values: payload.portfolio_values || [],
          period_returns:  payload.period_returns  || [],
          start_weights:   payload.start_weights   || [],
          end_weights:     payload.end_weights     || [],
        });
      } catch (err) {
        if (stillExists()) this.setResult(scenarioId, key, null);
        console.error('Stress test fetch failed', { scenarioId, key, err });
      } finally {
        if (stillExists()) this.setLoading(scenarioId, key, false);
      }
    },
  },
};
</script>

<style scoped>
/* Optional: tighter table text on small screens */
@media (max-width: 768px) {
  table { font-size: 0.875rem; }
}
</style>
